import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import MDBox from 'components/MDBox';
import MDBadge from 'components/MDBadge';
import MDTypography from 'components/MDTypography';
import Input from '@mui/material/Input';

import { formatPrice, calculateDiscount } from 'helpers/priceHelpers';
import { updateGroup } from 'services/api/project';
import { notify } from 'helpers/notificationHelper';
import { debounce } from 'helpers/debounce';

function BadgeContent({ price, quantity, discount }) {
    const discountedPrice = calculateDiscount(price, quantity, discount);
    return (
        <MDBadge
            size="xs"
            color={'light'}
            badgeContent={formatPrice(discountedPrice)}
            container
            sx={{
                '& .MuiBadge-badge': {
                    fontSize: '0.6rem',
                    minWidth: '18px',
                    height: '18px',
                    padding: '0 3px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '80px',
                },
            }}
        />
    );
}

function BadgeType({ type }) {
    const badgeColor = getBadgeColor(type);
    return (
        <MDBadge
            size="xs"
            color={badgeColor}
            badgeContent={type}
            container
            sx={{
                '& .MuiBadge-badge': {
                    fontSize: '0.6rem',
                    minWidth: '18px',
                    height: '18px',
                    padding: '0 3px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '60px',
                },
            }}
        />
    );
}

function InputField({ quantity, handleAmountUpdate, disabled }) {
    return (
        <Input
            id="standard-adornment-weight"
            aria-describedby="standard-weight-helper-text"
            inputProps={{
                'aria-label': 'weight',
                type: 'number',
                min: 0,
                style: { textAlign: 'center' },
            }}
            sx={{
                width: '100%',
                border: '0 1 0 0 solid black',
                fontSize: '0.875rem',
                '& input': {
                    padding: '4px 8px',
                },
            }}
            value={quantity}
            onChange={handleAmountUpdate}
            disabled={disabled}
        />
    );
}

function Card({ item, groupID, demandID, columnItems }) {
    const isDefaultGroup = groupID === 'defaultID';
    const { ref = {}, quantity = 0, type } = item;
    const [itemQuantity, setItemQuantity] = useState(quantity);
    const [isHovered, setIsHovered] = useState(false);

    const [inputValue, setInputValue] = useState(quantity); // Ayrı bir state oluşturduk
    const [hideCard, setHideCard] = useState('');
    const queryClient = useQueryClient();

    useEffect(() => {
        setInputValue(quantity);
        setItemQuantity(quantity);
    }, [quantity]);

    const handleMouseEnter = () => {
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
    };

    const handleDelete = async () => {
        setHideCard('none');
        const updatedGroupItems = columnItems.filter((el) => el.type === type && el.ref._id !== ref._id);
        await updateGroupItems(groupID, { [type]: updatedGroupItems }, setHideCard);
        queryClient.invalidateQueries({ queryKey: ['fetchDemandGroups', demandID] });
    };

    const handleAmountUpdate = async (value) => {
        const intValue = Number(value);
        setItemQuantity(intValue);

        let sameTypes = columnItems.filter((el) => el.type === type);
        sameTypes.forEach((el) => {
            if (el.ref._id === ref._id) el.quantity = intValue;
        });
        await updateGroupItems(groupID, { [type]: sameTypes });
        queryClient.invalidateQueries({ queryKey: ['fetchDemandGroups', demandID] });
    };

    // Yeni bir debounce fonksiyonu oluşturduk ve bu fonksiyonu useCallback ile sarmaladık.
    // Bu, her render işlemi sırasında yeni bir debounce fonksiyonu oluşturmayı önler.
    const debouncedHandleAmountUpdate = useCallback(
        debounce((value) => {
            handleAmountUpdate(value);
        }, 1000),
        [groupID, type, columnItems, ref._id, demandID]
    );

    const handleInputChange = (e) => {
        setInputValue(e.target.value); // Input alanını hemen güncelliyoruz
        debouncedHandleAmountUpdate(e.target.value); // Değişiklikler debounce ediliyor
    };

    let displayText = type == 'diger' ? ref.baslik + ' - ' + ref.ekNot : ref.baslik;
    if (!isHovered && displayText.length > 20) {
        displayText = displayText.substring(0, 20) + '...';
    }

    return (
        <MDBox
            sx={{
                display: hideCard,
                width: '100%',
                minWidth: 0,
                overflow: 'hidden',
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {/* <MDBox display="flex" alignItems="center" color="text" justifyContent="space-between">
                <BadgeContent price={ref.verilenFiyat} quantity={quantity} discount={ref.indirimOrani} />
                <BadgeType type={type} />
            </MDBox> */}
            <MDBox
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
                sx={{
                    flexWrap: 'nowrap',
                    minWidth: 0,
                    width: '100%',
                    flexDirection: 'row',
                    gap: 0.5,
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <MDBox display="flex" alignItems="center" sx={{ gap: 0.5, flexShrink: 0 }}>
                    <MDBox
                        sx={{
                            minWidth: '55px',
                            maxWidth: '70px',
                            flexShrink: 0,
                        }}
                    >
                        <InputField
                            quantity={inputValue}
                            handleAmountUpdate={handleInputChange}
                            disabled={groupID === 'defaultID'}
                        />
                    </MDBox>
                    {groupID != 'defaultID' && (
                        <IconButton aria-label="delete" size="small" onClick={handleDelete} sx={{ flexShrink: 0 }}>
                            <DeleteIcon fontSize="inherit" />
                        </IconButton>
                    )}
                </MDBox>

                <MDBox
                    display="flex"
                    alignItems="center"
                    sx={{ gap: 0.25, flexShrink: 1, minWidth: 0, overflow: 'hidden' }}
                >
                    <BadgeContent price={ref.verilenFiyat} quantity={quantity} discount={ref.indirimOrani} />
                    <BadgeType type={type} />
                </MDBox>
            </MDBox>

            <MDBox
                display="flex"
                sx={{
                    width: '100%',
                    minWidth: 0,
                    mt: 0.5,
                }}
            >
                <MDTypography
                    variant="caption"
                    fontWeight="light"
                    textTransform="lowercase"
                    sx={{
                        lineHeight: '1.1',
                        width: '100%',
                        wordBreak: 'break-word',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: isHovered ? 'normal' : 'nowrap',
                        fontSize: '0.75rem',
                    }}
                >
                    {displayText}
                </MDTypography>
            </MDBox>
        </MDBox>
    );
}

async function updateGroupItems(changedItemID, updateParams, setHideCard) {
    try {
        const response = await updateGroup(changedItemID, updateParams);
        notify(response.data.success, response.data.message);
        if (!response.data.success) setHideCard('');
    } catch (err) {
        notify(false, JSON.stringify(err));
        setHideCard('');
    }
}

function getBadgeColor(type) {
    const badgeColors = {
        uKasa: 'info',
        uPervaz: 'success',
        pervaz: 'error',
        kanat: 'dark',
        kasa: 'warning',
        diger: 'primary',
    };
    return badgeColors[type] || 'primary';
}

export default Card;
